import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert"
import { cn } from "../lib/utils"

export function AlertRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("alert")
  const resolvedVariantRaw = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const variant = resolvedVariantRaw as "default" | "destructive"

  // Compound mode: children are alert-title, alert-description
  const hasCompoundChildren = element.children?.some(
    (c) => c.type === "alert-title" || c.type === "alert-description"
  )

  const renderAlertChild = (rawChild: CogoElement) => {
    // JITRULE Task 2: alert-title/alert-description children may nest
    // `text` under `props`.
    const child = liftV4Props(rawChild)
    const resolvedText = child.text ? String(resolveSymbol(child.text) ?? "") : ""
    switch (child.type) {
      case "alert-title":
        return (
          <AlertTitle key={child.id} className={cn(child.className)} data-cogo-element-id={child.id} data-cogo-type={child.type}>
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </AlertTitle>
        )
      case "alert-description":
        return (
          <AlertDescription key={child.id} className={cn(child.className)} data-cogo-element-id={child.id} data-cogo-type={child.type}>
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </AlertDescription>
        )
      default:
        return <ElementRenderer key={child.id} element={child} />
    }
  }

  if (hasCompoundChildren) {
    return (
      <Alert variant={variant} className={cn(element.className)} data-testid={`alert-${element.id}`} data-atom-type="alert" data-variant={resolvedVariantRaw} data-size={resolvedSize}>
        {element.children?.map((child) => renderAlertChild(child))}
      </Alert>
    )
  }

  // Legacy mode
  const titleRaw = (element as { title?: unknown }).title ?? element.text
  const descriptionRaw = (element as { description?: unknown }).description
  const title = titleRaw ? String(resolveSymbol(String(titleRaw)) ?? "") : ""
  const firstChild = element.children?.[0] ? liftV4Props(element.children[0]) : undefined
  const description = descriptionRaw
    ? String(resolveSymbol(String(descriptionRaw)) ?? "")
    : firstChild?.text
    ? String(resolveSymbol(firstChild.text) ?? "")
    : ""

  return (
    <Alert variant={variant} className={cn(element.className)} data-testid={`alert-${element.id}`} data-atom-type="alert" data-variant={resolvedVariantRaw} data-size={resolvedSize}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {description && <AlertDescription>{description}</AlertDescription>}
    </Alert>
  )
}
